/**
 * SQL Query Configuration Component
 */

import * as React from 'react';
import { 
  Button,
  Badge,
  Textarea,
  ScrollArea
} from '../../../../components/ui';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle
} from '../../../../components/ui/resizable';
import { toast } from '../../../../components/ui/toast';
import { 
  ChevronDown, 
  ChevronUp, 
} from 'lucide-react';
import { 
  SQLQueryOptions
} from '../../../../lib/engine';
import { SQLQueryConfigProps } from './types';
import { formatSchemaType } from '../../data-grid/type-utils';

/**
 * SQL Query Configuration Component
 */
export function SQLQueryConfig({ query, updateQuery, columns }: SQLQueryConfigProps) {
  // Initialize state hooks first to ensure they're always rendered
  const [showSchemaReference, setShowSchemaReference] = React.useState(true);
  
  // Track expanded state for each table
  const [expandedTables, setExpandedTables] = React.useState<Record<string, boolean>>({
    'data': true,
    'users': false,
    'orders': false
  });
  
  // Check if we should render the component
  if (query.type !== 'SQL') return null;
  
  const options = query.options as SQLQueryOptions;
  
  // Toggle expanded state for a table
  const toggleTableExpanded = (tableName: string) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };
  
  // Handle SQL query changes
  const handleSqlChange = (sql: string) => {
    try {
      if (!updateQuery) return;
      
      // Update the SQL query
      updateQuery({
        ...query,
        options: {
          ...options,
          sql
        }
      });
    } catch (error) {
      // Show error in toast notification
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An error occurred while updating the SQL query';
      
      toast.error(errorMessage, {
        description: 'Please check your SQL syntax and try again.',
        duration: 5000,
        closeButton: true,
      });
      
      console.error('Error updating SQL query:', error);
    }
  };
  
  // Handle keyboard events in the SQL editor
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle tab key to insert spaces instead of changing focus
    if (e.key === 'Tab') {
      e.preventDefault();
      
      // Get the textarea element
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      // Insert 2 spaces at cursor position
      const newValue = textarea.value.substring(0, start) + '  ' + textarea.value.substring(end);
      
      // Update the value and cursor position
      handleSqlChange(newValue);
      
      // Set cursor position after the inserted spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };
  
  // Example tables for demonstration
  const exampleTables = [
    {
      name: 'data',
      columns: columns
    },
    {
      name: 'users',
      columns: [
        { name: 'id', schema: { type: 'int32' } },
        { name: 'username', schema: { type: 'utf8' } },
        { name: 'email', schema: { type: 'utf8' } },
        { name: 'created_at', schema: { type: 'timestamp' } },
        { name: 'active', schema: { type: 'bool' } }
      ]
    },
    {
      name: 'orders',
      columns: [
        { name: 'order_id', schema: { type: 'int32' } },
        { name: 'user_id', schema: { type: 'int32' } },
        { name: 'product_id', schema: { type: 'int32' } },
        { name: 'quantity', schema: { type: 'int32' } },
        { name: 'price', schema: { type: 'float64' } },
        { name: 'order_date', schema: { type: 'timestamp' } }
      ]
    }
  ];
  
  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-sm font-medium">SQL Query</h4>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowSchemaReference(!showSchemaReference)}
          className="text-xs"
        >
          {showSchemaReference ? "Hide Schema" : "Show Schema"}
        </Button>
      </div>
      
      {showSchemaReference ? (
        <ResizablePanelGroup
          direction="horizontal"
          className="flex-1 overflow-hidden"
        >
          <ResizablePanel defaultSize={70} minSize={30} className="overflow-hidden">
            <Textarea
              placeholder="Enter SQL query..."
              value={options.sql}
              onChange={(e) => handleSqlChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-full font-mono w-full resize-none"
              style={{ minHeight: "300px" }}
            />
          </ResizablePanel>
          
          <ResizableHandle withHandle />
          
          <ResizablePanel defaultSize={30} minSize={20} className="overflow-hidden">
            <ScrollArea className="h-full border rounded-md p-2 bg-muted/10 text-sm">
              <h5 className="font-medium mb-2">Available Tables</h5>
              <div className="space-y-3">
                {exampleTables.map((table) => (
                  <div key={table.name} className="border rounded-md overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-2 bg-muted/20 cursor-pointer hover:bg-muted/30"
                      onClick={() => toggleTableExpanded(table.name)}
                    >
                      <div className="flex items-center gap-1">
                        <span className="font-mono font-medium">{table.name}</span>
                        <Badge variant="outline" className="text-xs">table</Badge>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({table.columns.length} columns)
                        </span>
                      </div>
                      <div>
                        {expandedTables[table.name] ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </div>
                    
                    {expandedTables[table.name] && (
                      <div className="p-2 bg-background border-t">
                        <div className="ml-2 space-y-1">
                          {table.columns.map((column) => (
                            <div key={`${table.name}-${column.name}`} className="flex items-center gap-1 text-xs">
                              <span className="font-mono">{column.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({formatSchemaType(column.schema?.type)})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                <div className="text-xs text-muted-foreground italic border-t pt-2 mt-2">
                  <p className="font-medium mb-1">Example queries:</p>
                  <pre className="bg-muted/20 p-1 rounded text-[10px] overflow-x-auto">
                    SELECT * FROM data LIMIT 100
                  </pre>
                  <pre className="bg-muted/20 p-1 rounded text-[10px] mt-1 overflow-x-auto">
                    SELECT u.username, COUNT(o.order_id) as order_count 
                    FROM users u
                    JOIN orders o ON u.id = o.user_id
                    GROUP BY u.username
                  </pre>
                </div>
              </div>
            </ScrollArea>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <Textarea
          placeholder="Enter SQL query..."
          value={options.sql}
          onChange={(e) => handleSqlChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 font-mono w-full resize-none"
          style={{ minHeight: "300px", height: "100%" }}
        />
      )}
    </div>
  );
} 